import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Bell, Calendar, Check, ClipboardList } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notificationsApi, type AppNotification } from '../../services/api';
import clsx from 'clsx';

const LAST_SEEN_KEY = 'notifications_last_seen';

const TYPE_ICONS: Record<AppNotification['type'], ReactNode> = {
  meeting_today: <Calendar size={14} className="text-purple-500" />,
  interview_today: <Calendar size={14} className="text-green-500" />,
  interview_scheduled: <Calendar size={14} className="text-blue-500" />,
  task_assigned: <ClipboardList size={14} className="text-orange-500" />,
};

function isUnread(n: AppNotification, lastSeen: string): boolean {
  if (n.is_today) {
    const today = new Date().toISOString().split('T')[0];
    const lastSeenDate = lastSeen.split('T')[0];
    return lastSeenDate < today;
  }
  return n.created_at > lastSeen;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState(
    () => localStorage.getItem(LAST_SEEN_KEY) || '1970-01-01T00:00:00Z'
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const notifications = data?.data || [];
  const unreadCount = notifications.filter(n => isUnread(n, lastSeen)).length;

  const handleToggle = () => {
    if (!open) {
      const now = new Date().toISOString();
      localStorage.setItem(LAST_SEEN_KEY, now);
      setLastSeen(now);
    }
    setOpen(s => !s);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={handleToggle}
        className="relative p-1.5 text-white/80 hover:text-white transition-colors"
        aria-label="Notificações"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 text-sm">Notificações</h3>
            {notifications.length > 0 && (
              <span className="text-xs text-gray-400">{notifications.length} item{notifications.length !== 1 ? 's' : ''}</span>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Check size={24} className="text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Tudo em dia!</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
              {notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => { setOpen(false); navigate(n.path); }}
                  className={clsx(
                    'w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3',
                    isUnread(n, lastSeen) && 'bg-blue-50/40'
                  )}
                >
                  <div className="mt-0.5 flex-shrink-0">{TYPE_ICONS[n.type]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 leading-snug">{n.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{n.body}</p>
                  </div>
                  {isUnread(n, lastSeen) && (
                    <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
